import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AlertTriangle, ArrowLeft, Ban, Loader2, Trash2 } from "lucide-react";
import { toast } from "sonner";

import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import Layout from "../components/Layout";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";

import { api, getUser, logout } from "../lib/api";
import { listBlocks, unblockUser } from "../lib/moderation";
import AboutCard from "../components/AboutCard";

/**
 * User settings page. Currently hosts the Danger Zone (Delete Account).
 * Kept minimal on purpose so App Store review can find it in one tap
 * from the profile menu — as required by Apple Guideline 5.1.1(v).
 */
export default function Settings() {
  const user = getUser();
  const userId = user?.id;
  const navigate = useNavigate();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [blocks, setBlocks] = useState([]);
  const [blocksLoading, setBlocksLoading] = useState(true);

  useEffect(() => {
    if (!userId) return;
    listBlocks({ userId })
      .then(setBlocks)
      .catch(() => {})
      .finally(() => setBlocksLoading(false));
  }, [userId]);

  const doUnblock = async (blockedId, name) => {
    try {
      await unblockUser({ userId, blockedUserId: blockedId });
      setBlocks((prev) => prev.filter((b) => b.blocked_id !== blockedId));
      toast.success(`${name} has been unblocked.`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not unblock");
    }
  };

  if (!user) {
    // Not logged in — bounce to /login. This is a client-side gate; the
    // API also enforces auth via Bearer.
    navigate("/login", { replace: true });
    return null;
  }

  const performDelete = async () => {
    if (deleting) return;
    setDeleting(true);
    try {
      await api.delete("/users/me");
      toast.success("Your account has been permanently deleted.");
      logout();
      // Small delay lets the toast render before the route change.
      setTimeout(() => navigate("/", { replace: true }), 400);
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      // Backend guards against deletion while jobs are in flight — treat
      // that as a friendly info state, not a crash.
      if (status === 409 && detail?.message) {
        toast.error(detail.message);
      } else if (status === 409) {
        toast.error(
          "You cannot delete your account while you have active jobs. Please complete or cancel your pending jobs first."
        );
      } else {
        toast.error(detail?.message || detail || "Failed to delete account. Please try again.");
      }
      setConfirmOpen(false);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Layout>
      <div className="max-w-3xl mx-auto px-5 py-10 sm:py-14">
        <button
          type="button"
          data-testid="settings-back"
          onClick={() => navigate(-1)}
          className="inline-flex items-center gap-1.5 text-sm text-[#6B6965] hover:text-[#1B3624] mb-6 min-h-[44px]"
        >
          <ArrowLeft className="w-4 h-4" /> Back
        </button>
        <div>
        <h1
          data-testid="settings-title"
          className="text-3xl sm:text-4xl tracking-tighter font-medium"
          style={{ fontFamily: "Outfit" }}
        >
          Settings
        </h1>
        <p className="mt-2 text-[#6B6965]">
          Manage your Binshare account. Signed in as{" "}
          <span className="text-[#1B3624] font-medium">{user.email}</span>.
        </p>
      </div>

      {/* ---------- Blocked users (Guideline 1.2) ---------- */}
      <section className="mt-10" data-testid="blocked-users-section">
        <div className="flex items-center gap-2">
          <Ban className="w-4 h-4 text-[#6B6965]" />
          <div className="text-[11px] uppercase tracking-widest text-[#6B6965] font-medium">
            Blocked users
          </div>
        </div>
        <Card className="mt-3 rounded-2xl border-[#E2DFD8] p-6 bg-white">
          {blocksLoading ? (
            <div className="text-sm text-[#6B6965]">Loading…</div>
          ) : blocks.length === 0 ? (
            <div
              data-testid="blocked-users-empty"
              className="text-sm text-[#6B6965]"
            >
              You haven&apos;t blocked anyone. Blocking someone stops them from messaging you and hides them from your inbox.
            </div>
          ) : (
            <ul className="divide-y divide-[#E2DFD8]">
              {blocks.map((b) => (
                <li
                  key={b.id}
                  data-testid={`blocked-user-${b.blocked_id}`}
                  className="py-3 flex items-center gap-3"
                >
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-[#1B3624] truncate">
                      {b.blocked_name}
                    </div>
                    <div className="text-xs text-[#6B6965]">
                      Blocked {new Date(b.created_at).toLocaleDateString("en-GB")}
                      {b.reason ? ` · ${b.reason}` : ""}
                    </div>
                  </div>
                  <Button
                    data-testid={`unblock-${b.blocked_id}`}
                    variant="outline"
                    size="sm"
                    onClick={() => doUnblock(b.blocked_id, b.blocked_name)}
                    className="rounded-full"
                  >
                    Unblock
                  </Button>
                </li>
              ))}
            </ul>
          )}
        </Card>
      </section>

      {/* ---------- Danger Zone ---------- */}
      <section className="mt-10">
        <div className="flex items-center gap-2">
          <AlertTriangle className="w-4 h-4 text-red-600" />
          <div
            className="text-[11px] uppercase tracking-widest text-red-700 font-medium"
            data-testid="danger-zone-label"
          >
            Danger Zone
          </div>
        </div>

        <Card
          data-testid="danger-zone-card"
          className="mt-3 rounded-2xl border-2 border-red-200 bg-red-50/40 p-6"
        >
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
            <div className="min-w-0">
              <h2
                className="text-lg font-medium text-red-900"
                style={{ fontFamily: "Outfit" }}
              >
                Delete my account
              </h2>
              <p className="mt-1 text-sm text-red-800 max-w-xl">
                Permanently delete your Binshare account and all associated personal data.
                This action <strong>cannot be undone</strong>.
              </p>
              <ul className="mt-3 text-xs text-red-900/80 list-disc pl-5 space-y-0.5">
                <li>Your open posts and bids are deleted.</li>
                <li>
                  Historical records (completed jobs) are kept for the other party&apos;s business
                  history, but your name is replaced with &ldquo;Deleted User&rdquo; and your
                  email &amp; phone number are removed.
                </li>
                <li>All your messages and notifications are erased.</li>
                <li>You cannot delete while a job is in progress or money is in escrow.</li>
              </ul>
            </div>
            <Button
              data-testid="delete-account-btn"
              variant="destructive"
              onClick={() => setConfirmOpen(true)}
              className="rounded-full bg-red-600 hover:bg-red-700 text-white min-h-[44px] px-5 shrink-0"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Delete Account
            </Button>
          </div>
        </Card>
      </section>

      <AboutCard />

      <Dialog open={confirmOpen} onOpenChange={(o) => !deleting && setConfirmOpen(o)}>
        <DialogContent data-testid="delete-account-modal" className="max-w-md rounded-2xl">
          <DialogHeader>
            <DialogTitle className="text-red-700 flex items-center gap-2">
              <AlertTriangle className="w-5 h-5" />
              Are you sure?
            </DialogTitle>
            <DialogDescription className="pt-2 text-[#1A1A1A] text-[15px] leading-relaxed">
              This will permanently delete your account and all associated data.
              <br />
              <strong>This action cannot be undone.</strong>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="mt-2 gap-2 sm:gap-2">
            <Button
              data-testid="delete-account-cancel"
              variant="outline"
              onClick={() => setConfirmOpen(false)}
              disabled={deleting}
              className="rounded-full min-h-[44px]"
            >
              Cancel
            </Button>
            <Button
              data-testid="delete-account-confirm"
              onClick={performDelete}
              disabled={deleting}
              className="rounded-full bg-red-600 hover:bg-red-700 text-white min-h-[44px]"
            >
              {deleting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Deleting…
                </>
              ) : (
                <>
                  <Trash2 className="w-4 h-4 mr-2" /> Delete My Account
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      </div>
    </Layout>
  );
}
